import React, { useEffect, useMemo, useState } from "react";
import useUser from "../../../hooks/useUser";
import { getOrganization, updateOrganization } from "../../../services/organizationService";
import { getUserCookie, setUserCookie } from "../../../context";
import ButtonSave from "../../components/Buttons/ButtonSave/ButtonSave";
import ButtonCancel from "../../components/Buttons/ButtonCancel/ButtonCancel";
import { useTranslation } from "react-i18next";
import "./Organization.css";

export default function Organization() {
  const { user } = useUser();
  const { t } = useTranslation("organization");

  const organizationId = useMemo(() => {
    const org = user?.organization || user?.organization_id;
    if (!org) return null;
    if (typeof org === "object") {
      return org.id || org._id || null;
    }
    return org;
  }, [user?.organization, user?.organization_id]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    prompt_contextual_role: "",
    prompt_objective: "",
    prompt_constraints: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setSuccess(false);
      try {
        const data = await getOrganization(organizationId);
        if (cancelled || !data) return;
        setForm({
          name: data.name || "",
          description: data.description || "",
          prompt_contextual_role: data.prompt_contextual_role || "",
          prompt_objective: data.prompt_objective || "",
          prompt_constraints: data.prompt_constraints || "",
        });
      } catch (e) {
        if (!cancelled) {
          console.error("Error fetching organization", e?.response || e);
          setError(t("page.errorLoading"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [organizationId, t]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!organizationId) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateOrganization(organizationId, {
        name: form.name,
        description: form.description,
        prompt_contextual_role: form.prompt_contextual_role,
        prompt_objective: form.prompt_objective,
        prompt_constraints: form.prompt_constraints,
      });

      // Sincronizar el nombre de la organización también en la cookie de usuario
      // para que el contexto se inicialice con el valor actualizado al recargar
      try {
        const userCookie = getUserCookie();
        if (userCookie) {
          const updatedUser = {
            ...userCookie,
            organization_id: organizationId,
            organization: {
              ...(userCookie.organization || {}),
              id: organizationId,
              name: form.name,
            },
          };
          setUserCookie(updatedUser);

          // Si el App expuso un setter global, actualizamos también el contexto
          // en memoria para que el cambio se vea inmediatamente en Profile.
          try {
            if (typeof window !== "undefined" && window.owlyManageSetUser) {
              window.owlyManageSetUser(updatedUser);
            }
          } catch (_) {}
        }
      } catch (cookieError) {
        console.warn("Failed to sync organization name to user cookie", cookieError);
      }

      setSuccess(true);
    } catch (e) {
      console.error("Error updating organization", e?.response || e);
      setError(t("page.errorUpdating"));
    } finally {
      setSaving(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="organization-page">
        <h1 className="organization-title">{t("page.title")}</h1>
        <p>{t("page.noOrganization")}</p>
      </div>
    );
  }

  return (
    <div className="organization-page">
      <h1 className="organization-title">{t("page.title")}</h1>
      <p className="organization-subtitle">
        {t("page.subtitle")}
      </p>

      {loading && <p>{t("page.loading")}</p>}

      {!loading && (
        <form className="organization-form" onSubmit={handleSubmit}>
          {error && <div className="organization-error">{error}</div>}
          {success && (
            <div className="organization-success">
              {t("page.successUpdating")}
            </div>
          )}

          <div className="organization-field">
            <label className="organization-label">{t("page.fields.name")}</label>
            <input
              className="organization-textarea"
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </div>

          <div className="organization-field">
            <label className="organization-label">{t("page.fields.description")}</label>
            <textarea
              className="organization-textarea"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
            />
          </div>

          <div className="organization-field">
            <label className="organization-label">{t("page.fields.contextualRole")}</label>
            <textarea
              className="organization-textarea"
              value={form.prompt_contextual_role}
              onChange={(e) =>
                handleChange("prompt_contextual_role", e.target.value)
              }
              rows={3}
            />
          </div>

          <div className="organization-field">
            <label className="organization-label">{t("page.fields.objective")}</label>
            <textarea
              className="organization-textarea"
              value={form.prompt_objective}
              onChange={(e) => handleChange("prompt_objective", e.target.value)}
              rows={3}
            />
          </div>

          <div className="organization-field">
            <label className="organization-label">{t("page.fields.constraints")}</label>
            <textarea
              className="organization-textarea"
              value={form.prompt_constraints}
              onChange={(e) =>
                handleChange("prompt_constraints", e.target.value)
              }
              rows={3}
            />
          </div>

          <div className="organization-actions">
            <ButtonCancel
              type="button"
              onClick={() => window.history.back()}
              ariaLabel={t("page.buttons.cancel")}
              disabled={saving}
            />
            <ButtonSave
              type="submit"
              ariaLabel={t("page.buttons.save")}
              isEdit={true}
              isLoading={saving}
              disabled={saving}
            />
          </div>
        </form>
      )}
    </div>
  );
}
